import { set, useLazyCopy } from '@seiue/util'
import {
  assign,
  isEqual,
  isFunction,
  parseURLQuery,
  pick,
  pickBy,
  stringifyURLQuery,
  useForceUpdate,
} from '@seiue/util'
import { MODAL_QUERY_NAMESPACE, Query } from 'packages/route/types'
import React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

/**
 * 一个简易的获取/设置表单值的 hook，用在非常简单的表单中
 *
 * @param initialValues - 初始值
 * @returns [formValues, setFieldValue, resetValues]
 */
export const useFormValues = <T extends object>(initialValues: T) => {
  const valuesCopy = useLazyCopy(initialValues)
  const [formValues, setFormValues] = React.useState<T>(valuesCopy)

  React.useEffect(() => {
    setFormValues(valuesCopy)
  }, [valuesCopy])

  // 有些地方的依赖项会用到 setFieldValue
  const setFieldValue = React.useCallback(
    (fieldName: string, fieldValue: any) => {
      setFormValues((prev) => {
        const newValues = { ...prev }
        set(newValues, fieldName, fieldValue)
        return newValues
      })
    },
    []
  )

  const resetValues = () => setFormValues({ ...initialValues })

  return [formValues, setFieldValue, resetValues] as const
}

export type SetQueryProp<TExtendedQuery> =
  | TExtendedQuery
  | ((lastQuery: { [key: string]: any }) => TExtendedQuery)

/**
 * @util
 * 读取和设置 url query 的 hook，
 * 会自动忽略 modalQuery，
 * 在 url query 长度可能超过 2000 时转为使用 local state，不再更新 url
 */
// export const useUrlQuery = <
//   /**
//    * 指定 TQuery 可使 hook 返回的 query 和 setQuery 类型
//    * 都限定为 TQuery & { [key: string]: any }，
//    * 可进而使 query.xxx 被使用时有正确类型。
//    *
//    * 例：const [query, setQuery] = useUrlQuery<QueryStudentsQuery>()
//    */
//   TQuery = Query,
//   TExtendedQuery extends TQuery & Query = TQuery & Query
// >(
//   defaultQuery = {}
// ) => {
//   const location = useLocation()
//   const history = useHistory()
//   const { replace } = history
//   const [forceUpdate] = useForceUpdate()
//   const longSearchRef = useRef<string>('')

//   const [stableDefaultQuery, setStableDefaultQuery] = useState(defaultQuery)

//   useEffect(() => {
//     if (!isEqual(stableDefaultQuery, defaultQuery))
//       setStableDefaultQuery(defaultQuery)
//   }, [stableDefaultQuery, defaultQuery])

//   return useMemo(() => {
//     const currentSearch = longSearchRef.current
//       ? longSearchRef.current
//       : location.search

//     const query = assign({}, stableDefaultQuery, parseURLQuery(currentSearch))

//     // 过滤掉 modal 与 modalQuery
//     const filterQuery = pickBy(
//       query,
//       (value, key) => key !== MODAL_QUERY_NAMESPACE && key !== 'modal'
//     ) as TExtendedQuery

//     const modalQuery = pick(query, [MODAL_QUERY_NAMESPACE, 'modal'])

//     const setQuery = (prop: SetQueryProp<TExtendedQuery>) => {
//       const newQuery = isFunction(prop) ? prop(query) : prop

//       const nextQuery = {
//         ...modalQuery,
//         ...newQuery,
//       }

//       const search = stringifyURLQuery(nextQuery)

//       // 2000 接近 safari 和一些旧版浏览器的最大 url 长度
//       if (search.length > 2000) {
//         longSearchRef.current = search

//         forceUpdate()
//       } else {
//         longSearchRef.current = ''

//         replace({
//           search,
//         })
//       }
//     }

//     return [filterQuery, setQuery] as const
//   }, [location.search, replace, stableDefaultQuery, forceUpdate])
// }
